import React, {
  ReactNode, useCallback, useMemo, useState,
} from 'react';
import {
  Form, InputGroup, FormControl, FloatingLabel,
} from 'react-bootstrap';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FieldMetaState } from 'react-final-form';
import Select from 'react-select';
import _ from 'lodash';
import { SelectValue } from 'types/Select';

type SelectProps = {
  value: SelectValue;
  label?: string | ReactNode;
  idKey?: string,
  labelKey?: string,
  translateLabel?: boolean;
  placeholder?: string;
  options: any[];
  validateTranslateValues?: any;
  translateOptions?: boolean;
  classNames?: {
    input?: string;
    formGroup?: string;
    label?: string;
  };
  inputOnChange?: ( value: SelectValue ) => void
  onChange?: ( value: SelectValue ) => void
  showErrors?: boolean;
  isInvalid?: boolean;
  isMulti?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  meta?: FieldMetaState<any>;
};

function SelectInput( {
  value,
  label,
  idKey = 'id',
  labelKey = 'name',
  placeholder,
  options,
  translateLabel = true,
  translateOptions,
  validateTranslateValues,
  classNames = {},
  inputOnChange,
  onChange,
  isInvalid,
  isMulti,
  isLoading,
  isDisabled,
  showErrors = true,
  meta,
  isClearable,
}: SelectProps ) {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState( false );

  const formattedOptions = useMemo( () => _.map( options, ( option ) => ( {
    value: _.get( option, idKey ),
    label: translateOptions ? t( _.get( option, labelKey ) ) : _.get( option, labelKey ),
    original: option,
  } ) ), [options] );

  const getLabel = useCallback( ( val: string | number ) => _.find( options, {
    [idKey]: val,
  } )?.[labelKey], [options, idKey, labelKey] );

  const formatMultipleValues = useCallback( () => _.map(
    value as Array<string | number>,
    ( item ) => ( {
      value: item,
      label: t( getLabel( item ) ),
    } ),
  ), [value, options] );

  const formattedValue = useCallback( () => (
    isMulti ? formatMultipleValues()
      : {
        value,
        label: t( getLabel( value as string | number ) ),
      } ), [isMulti, value, options] );

  const hasValue = isMulti
    ? ( Array.isArray( value ) && value.length > 0 )
    : !!value;

  const renderField = () => (
    <Select
      value={value ? formattedValue() : null}
      placeholder={placeholder ? t( placeholder ) : ''}
      classNamePrefix="custom-select"
      className={classnames( 'custom-select__container form-control', {
        'is-invalid': isInvalid,
        'has-value': hasValue,
        'has-focus': isFocused,
      } )}
      onFocus={() => setIsFocused( true )}
      onBlur={() => setIsFocused( false )}
      onChange={( val: any ) => {
        let newValue: SelectValue;
        if ( val === undefined || val === null
          || ( isMulti && !( val as Array<any> ).length ) ) {
          newValue = null;
        } else if ( isMulti ) {
          newValue = ( val as Array<any> ).map( ( item ) => item.value );
        } else if ( val.value !== value ) {
          newValue = val.value;
        }
        if ( inputOnChange ) inputOnChange( newValue );
        if ( onChange ) onChange( newValue );
      }}
      options={formattedOptions}
      isMulti={isMulti}
      isLoading={isLoading}
      isDisabled={isDisabled}
      isSearchable
      isClearable={isClearable}
    />
  );
  const labelRender = translateLabel && typeof label === 'string' ? t( label ) : label;
  return (
    <Form.Group className={classnames( 'form-group', classNames.formGroup, {
      'is-invalid': isInvalid,
    } )}
    >
      <InputGroup>
        {label
          ? (
            <FloatingLabel
              label={labelRender}
            >
              {renderField()}
            </FloatingLabel>
          )
          : ( renderField() )}
      </InputGroup>

      {showErrors
      && (
        <FormControl.Feedback type="invalid">
          {isInvalid ? t( meta?.error || meta?.submitError, validateTranslateValues ) || ' ' : ''}
        </FormControl.Feedback>
      )}
    </Form.Group>

  );
}

export default SelectInput;
